import axios from 'axios';
import { API_URL } from '../config';

// Account Automation always talks to the PRODUCTION backend, regardless of
// which environment this client build/dev-server is otherwise pointed at
// (see client/src/services/api.js for the normal per-env instance). Video
// downloads, S3 storage, pending-affiliate creation, and auto-blog creation
// all need to land on the real deployed (EC2) backend and its real S3
// bucket, even when you're running the dashboard locally against dev.
//
// Override with REACT_APP_AUTOMATION_API_URL if the production URL ever
// changes without a redeploy of this file.
export const AUTOMATION_API_URL =
  process.env.REACT_APP_AUTOMATION_API_URL || 'https://admin-api.explosionsocial.com/api';

// If THIS client build is already talking to production (API_URL, baked in
// at build time from REACT_APP_API_URL — true for the deployed admin portal
// itself), the main app's own login token IS already a production-issued
// token, since it came from signing into production in the first place.
// There's nothing separate to log into. The dedicated sign-in flow below only
// matters when this build's normal backend differs from production (running
// the dashboard locally against dev), since dev/prod don't share a JWT
// secret and a dev-issued token would 401 against production.
const normalize = (u) => (u || '').replace(/\/+$/, '');
export const IS_ALREADY_ON_PRODUCTION = normalize(API_URL) === normalize(AUTOMATION_API_URL);

// Deliberately a *different* localStorage key than the main app's `token` —
// only used in the "running locally" case. Dev and production have
// different JWT secrets, so a token from one is never valid on the other;
// keeping them separate means switching tabs never sends the wrong token to
// the wrong backend.
const AUTOMATION_TOKEN_KEY = 'automationProdToken';
const AUTOMATION_USER_KEY = 'automationProdUser';
const MAIN_TOKEN_KEY = 'token'; // matches client/src/context/AuthContext.js

export const automationApi = axios.create({
  baseURL: AUTOMATION_API_URL,
  withCredentials: true,
});

automationApi.interceptors.request.use((config) => {
  const token = IS_ALREADY_ON_PRODUCTION
    ? localStorage.getItem(MAIN_TOKEN_KEY)
    : localStorage.getItem(AUTOMATION_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Called by AccountAutomation.js to react to a 401 (no session / expired
// session) by re-opening the production sign-in dialog. Not used when
// IS_ALREADY_ON_PRODUCTION — see the response interceptor below.
let onAuthRequired = null;
export const setAutomationAuthHandler = (handler) => {
  onAuthRequired = handler;
};

automationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (IS_ALREADY_ON_PRODUCTION) {
        // The main app's own session is invalid — same handling as the main
        // app's own axios instance (utils/api.js): send them to sign in
        // again, rather than showing the "connect to production" dialog
        // that only makes sense for the running-locally case.
        localStorage.removeItem(MAIN_TOKEN_KEY);
        window.location.href = '/signin';
      } else {
        localStorage.removeItem(AUTOMATION_TOKEN_KEY);
        localStorage.removeItem(AUTOMATION_USER_KEY);
        if (onAuthRequired) onAuthRequired();
      }
    }
    return Promise.reject(error);
  }
);

// On the deployed production admin portal, being on this page at all already
// means ProtectedRoute verified a valid production admin session — trust it,
// no separate check needed. Only the running-locally case needs its own
// dedicated-token check.
export const isAutomationAuthenticated = () =>
  IS_ALREADY_ON_PRODUCTION
    ? !!localStorage.getItem(MAIN_TOKEN_KEY)
    : !!localStorage.getItem(AUTOMATION_TOKEN_KEY);

export const getAutomationUser = () => {
  if (IS_ALREADY_ON_PRODUCTION) {
    // No separate stored user for this case — AccountAutomation.js reads the
    // main app's own `user` from AuthContext instead when this is true.
    return null;
  }
  try {
    const raw = localStorage.getItem(AUTOMATION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Mirrors the main app's admin sign-in (client/src/pages/SignIn.js) —
// portal: 'admin' enforces the same admin-only role check server-side.
// Only relevant when NOT IS_ALREADY_ON_PRODUCTION.
export const automationLogin = async (email, password) => {
  const response = await automationApi.post('/auth/signin', { email, password, portal: 'admin' });
  const { token, user } = response.data || {};
  if (!token) {
    throw new Error('No token returned from production sign-in');
  }
  localStorage.setItem(AUTOMATION_TOKEN_KEY, token);
  if (user) localStorage.setItem(AUTOMATION_USER_KEY, JSON.stringify(user));
  return { token, user };
};

export const automationLogout = () => {
  localStorage.removeItem(AUTOMATION_TOKEN_KEY);
  localStorage.removeItem(AUTOMATION_USER_KEY);
};

export default automationApi;
